// src/pages/PoaForm.js
import React, { useState, useEffect } from 'react';
import {
  Container, TextField, Button, Paper, Typography, Box,
  FormControlLabel, Checkbox, MenuItem, Alert
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../api/axiosConfig';

const PoaForm = () => {
  const { id } = useParams(); // id del POA si es edición
  const navigate = useNavigate();
  const usuarioActual = localStorage.getItem('user');

  const [formData, setFormData] = useState({
    id_departamento: '',
    id_sector: '',
    id_programa: '',
    id_proyecto: '',
    descripcion: '',
    anno: new Date().getFullYear(),
    observaciones: '',
    activo: true,
  });

  const [sectores, setSectores] = useState([]);
  const [programas, setProgramas] = useState([]);
  const [proyectos, setProyectos] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Cargar catálogos iniciales
  useEffect(() => {
    const loadData = async () => {
      try {
        const [depRes, secRes] = await Promise.all([
          api.get('/departamentos/'),
          api.get('/sectores/'),
        ]);
        setDepartamentos(depRes.data);
        setSectores(secRes.data);
      } catch (err) {
        console.error('Error al cargar catálogos:', err);
        setError('Error al cargar datos iniciales');
      }
    };
    loadData();
  }, []);

  // Cargar programas al seleccionar sector
  useEffect(() => {
    if (formData.id_sector) {
      api.get(`/programas/?id_sector=${formData.id_sector}`)
        .then(res => setProgramas(res.data))
        .catch(err => console.error(err));
    } else {
      setProgramas([]);
      setFormData(prev => ({ ...prev, id_programa: '', id_proyecto: '' }));
    }
  }, [formData.id_sector]);

  // Cargar proyectos al seleccionar programa
  useEffect(() => {
    if (formData.id_programa) {
      api.get(`/proyectos/?id_programa=${formData.id_programa}`)
        .then(res => setProyectos(res.data))
        .catch(err => console.error(err));
    } else {
      setProyectos([]);
      setFormData(prev => ({ ...prev, id_proyecto: '' }));
    }
  }, [formData.id_programa]);

  // Cargar datos si es edición
  useEffect(() => {
    if (id) {
      setLoading(true);
      api.get(`/poa/${id}/`)
        .then(res => {
          setFormData(res.data);
        })
        .catch(err => {
          setError('Error al cargar el POA');
          console.error(err);
        })
        .finally(() => setLoading(false));
    }
  }, [id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validación básica
    if (!formData.id_departamento || !formData.id_sector || 
        !formData.id_programa || !formData.id_proyecto || 
        !formData.descripcion || !formData.anno) {
      setError('Todos los campos marcados con * son obligatorios');
      setLoading(false);
      return;
    }

    try {
      const payload = {
        ...formData,
        creado_por: usuarioActual,
        modificado_por: usuarioActual,
      };

      if (id) {
        // Edición: solo enviamos modificado_por
        await api.put(`/poa/${id}/`, {
          ...formData,
          modificado_por: usuarioActual
        });
      } else {
        // Creación
        await api.post('/poa/', payload);
      }

      navigate('/poa');
    } catch (err) {
      console.error('Error al guardar POA:', err.response || err);
      setError(err.response?.data?.detail || 'Error al guardar. Verifique los datos.');
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="lg">
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h5" gutterBottom>
          {id ? 'Editar POA' : 'Nuevo POA'}
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            select
            label="Departamento *"
            fullWidth
            value={formData.id_departamento}
            onChange={(e) => setFormData({ ...formData, id_departamento: e.target.value })}
            margin="normal"
            required
          >
            {departamentos.map(dep => (
              <MenuItem key={dep.id_departamento} value={dep.id_departamento}>
                {dep.descripcion}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Sector *"
            fullWidth
            value={formData.id_sector}
            onChange={(e) => setFormData({ ...formData, id_sector: e.target.value })}
            margin="normal"
            required
          >
            {sectores.map(sec => (
              <MenuItem key={sec.id_sector} value={sec.id_sector}>
                {sec.descripcion}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Programa *"
            fullWidth
            value={formData.id_programa}
            onChange={(e) => setFormData({ ...formData, id_programa: e.target.value })}
            margin="normal"
            required
            disabled={!formData.id_sector}
          >
            {programas.map(prog => (
              <MenuItem key={prog.id_programa} value={prog.id_programa}>
                {prog.descripcion}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Proyecto *"
            fullWidth
            value={formData.id_proyecto}
            onChange={(e) => setFormData({ ...formData, id_proyecto: e.target.value })}
            margin="normal"
            required
            disabled={!formData.id_programa}
          >
            {proyectos.map(proy => (
              <MenuItem key={proy.id_proyecto} value={proy.id_proyecto}>
                {proy.descripcion}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Descripción *"
            fullWidth
            value={formData.descripcion}
            onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
            margin="normal"
            required
          />

          <TextField
            label="Año *"
            type="number"
            fullWidth
            value={formData.anno}
            onChange={(e) => setFormData({ ...formData, anno: Number(e.target.value) })}
            margin="normal"
            required
          />

          <TextField
            label="Observaciones"
            multiline
            rows={3}
            fullWidth
            value={formData.observaciones}
            onChange={(e) => setFormData({ ...formData, observaciones: e.target.value })}
            margin="normal"
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={formData.activo}
                onChange={(e) => setFormData({ ...formData, activo: e.target.checked })}
              />
            }
            label="Activo"
          />

          <Box sx={{ mt: 2 }}>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              sx={{ mr: 1 }}
            >
              {loading ? 'Guardando...' : id ? 'Actualizar' : 'Guardar'}
            </Button>
            <Button onClick={() => navigate('/poa')}>
              Cancelar
            </Button>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default PoaForm;